import {
  type Crew,
  crewSchema,
  type MatchMetadata,
  matchMetadataSchema,
  type PersonName,
  type PremiumBrand,
  type ProjectState,
  WEATHER_CONDITIONS,
  type Weather,
  weatherConditionId,
  weatherSchema,
} from "@livcg/schema";
import { useEffect, useState } from "react";
import { sendRaw } from "../../shared/sendRaw.js";
import { Button, Card, cn, focusRing } from "../../ui/index.js";
import { DEFAULT_HINT, LeagueLogoPicker } from "./LeagueLogoPicker.js";

/** The weather icons the graphic uses (served from the overlay bundle). */
const NO_PREMIUM: PremiumBrand = { primary: null, accent: null, sponsorLogoUrl: null };
const WEATHER_ICON_BASE = "/bundles/livcg/graphics/overlay/assets";
const weatherIconUrl = (id: string) =>
  `${WEATHER_ICON_BASE}/weather_icon_${id === "moon" ? "time" : id}.svg`;

const INPUT =
  "h-8 w-full rounded-md border border-border bg-surface px-2 text-[13px] text-ink placeholder:text-ink-faint";

type Props = {
  slug: string;
  state: ProjectState;
  onClose: () => void;
  /** Rendered as a page tab (Klassik), not inside a drawer. */
  inline?: boolean;
};

/** SKORE-style match info editor: crew (officials), metadata (stadium / city /
 *  sponsors / universal lower-third) and pitch weather. Saved into the
 *  persistent project replicant via crew.set / metadata.set / weather.set; the
 *  names_*, coach_id_* and sponsor overlays read it when shown. */
export function SetupDrawer({ slug, state, onClose, inline = false }: Props) {
  const [crew, setCrew] = useState<Crew>(() => state.crew ?? crewSchema.parse({}));
  const [meta, setMeta] = useState<MatchMetadata>(
    () => state.metadata ?? matchMetadataSchema.parse({}),
  );
  const [weather, setWeather] = useState<Weather>(() => state.weather ?? weatherSchema.parse({}));

  const premium = meta.premium ?? NO_PREMIUM;
  const setPremium = (patch: Partial<PremiumBrand>) =>
    setMeta((m) => ({ ...m, premium: { ...(m.premium ?? NO_PREMIUM), ...patch } }));

  const teamColors = (side: "home" | "away") => ({
    primary: state.teams[side]?.colorPrimary ?? "#ffffff",
    secondary: state.teams[side]?.colorSecondary ?? "#000000",
  });
  const [colors, setColors] = useState(() => ({
    home: teamColors("home"),
    away: teamColors("away"),
  }));
  const [colorsDirty, setColorsDirty] = useState({ home: false, away: false });

  // A reset writes through the replicant — refresh non-dirty sides from it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally only re-syncs on state.teams; colorsDirty/teamColors are read fresh, not tracked, so resets don't fight local edits
  useEffect(() => {
    setColors((c) => ({
      home: colorsDirty.home ? c.home : teamColors("home"),
      away: colorsDirty.away ? c.away : teamColors("away"),
    }));
  }, [state.teams]);

  const setColor = (side: "home" | "away", key: "primary" | "secondary", v: string) => {
    setColors((c) => ({ ...c, [side]: { ...c[side], [key]: v } }));
    setColorsDirty((d) => ({ ...d, [side]: true }));
  };
  const resetColors = (side: "home" | "away") => {
    void sendRaw("team.setColors", { slug, side, primary: null, secondary: null });
    setColorsDirty((d) => ({ ...d, [side]: false }));
  };

  const save = () => {
    void sendRaw("crew.set", { slug, crew });
    void sendRaw("metadata.set", { slug, metadata: meta });
    void sendRaw("weather.set", { slug, weather });
    for (const side of ["home", "away"] as const) {
      if (colorsDirty[side]) {
        void sendRaw("team.setColors", {
          slug,
          side,
          primary: colors[side].primary,
          secondary: colors[side].secondary,
        });
      }
    }
    onClose();
  };

  const setPerson = (key: keyof Crew, v: PersonName | null) => setCrew((c) => ({ ...c, [key]: v }));

  const fields = (
    <>
      <p className="text-[12px] text-ink-muted">
        Bu ma'lumotlar saqlanadi va tegishli overlay (Names, Coach, Sponsor) efirga chiqarilganda
        avtomatik ishlatiladi. Hakamlar va murabbiylar PFL'dan avtomatik to'ladi — faqat BO'SH
        maydonlar, ya'ni siz yozganingiz hech qachon ustidan yozilmaydi. Sharhlovchilar PFL'da yo'q,
        ularni qo'lda kiriting.
      </p>

      <Section title="Hakamlar">
        <PersonRow
          label="Bosh hakam"
          value={crew.referee}
          onChange={(v) => setPerson("referee", v)}
        />
        <PersonRow
          label="Yordamchi 1"
          value={crew.refereeHelper1}
          onChange={(v) => setPerson("refereeHelper1", v)}
        />
        <PersonRow
          label="Yordamchi 2"
          value={crew.refereeHelper2}
          onChange={(v) => setPerson("refereeHelper2", v)}
        />
        <PersonRow
          label="4-hakam"
          value={crew.refereeHelper3}
          onChange={(v) => setPerson("refereeHelper3", v)}
        />
        <PersonRow label="VAR" value={crew.var} onChange={(v) => setPerson("var", v)} />
        <PersonRow label="AVAR" value={crew.avar} onChange={(v) => setPerson("avar", v)} />
      </Section>

      <Section title="Sharhlovchilar va murabbiylar">
        <PersonRow
          label="Sharhlovchi 1"
          value={crew.commentator1}
          onChange={(v) => setPerson("commentator1", v)}
        />
        <PersonRow
          label="Sharhlovchi 2"
          value={crew.commentator2}
          onChange={(v) => setPerson("commentator2", v)}
        />
        <PersonRow
          label="Murabbiy (Home)"
          value={crew.coachHome}
          onChange={(v) => setPerson("coachHome", v)}
        />
        <PersonRow
          label="Murabbiy (Away)"
          value={crew.coachAway}
          onChange={(v) => setPerson("coachAway", v)}
        />
      </Section>

      <Section title="Stadion va shahar">
        <Field label="Stadion">
          <input
            className={INPUT}
            value={meta.stadium ?? ""}
            onChange={(e) => setMeta((m) => ({ ...m, stadium: e.target.value || null }))}
            placeholder="Stadion nomi"
          />
        </Field>
        <Field label="Shahar">
          <input
            className={INPUT}
            value={meta.city ?? ""}
            onChange={(e) => setMeta((m) => ({ ...m, city: e.target.value || null }))}
            placeholder="Shahar"
          />
        </Field>
      </Section>

      <Section title="Jamoa ranglari">
        <p className="text-[12px] text-ink-faint">
          Score grafikadagi jamoa (jersey) ranglari. "PFL" tugmasi PFL'dan kelgan asl qiymatga
          qaytaradi.
        </p>
        {(["home", "away"] as const).map((side) => (
          <div key={side} className="grid grid-cols-[120px_1fr_1fr_auto] items-center gap-2">
            <span className="text-[12px] text-ink-muted">
              {state.teams[side]?.shortName ?? (side === "home" ? "Home" : "Away")}
            </span>
            <label className="flex items-center gap-2 text-[12px] text-ink-muted">
              <input
                type="color"
                value={colors[side].primary}
                onChange={(e) => setColor(side, "primary", e.target.value)}
                className="h-8 w-10 cursor-pointer rounded-md border border-border bg-surface p-0.5"
              />
              Asosiy
            </label>
            <label className="flex items-center gap-2 text-[12px] text-ink-muted">
              <input
                type="color"
                value={colors[side].secondary}
                onChange={(e) => setColor(side, "secondary", e.target.value)}
                className="h-8 w-10 cursor-pointer rounded-md border border-border bg-surface p-0.5"
              />
              Qo'shimcha
            </label>
            <Button variant="ghost" size="sm" onClick={() => resetColors(side)}>
              PFL
            </Button>
          </div>
        ))}
      </Section>

      <Section title="Homiylar">
        <SponsorRow
          label="Homiy 1"
          value={meta.sponsor1}
          onChange={(v) => setMeta((m) => ({ ...m, sponsor1: v }))}
        />
        <SponsorRow
          label="Homiy 2"
          value={meta.sponsor2}
          onChange={(v) => setMeta((m) => ({ ...m, sponsor2: v }))}
        />
      </Section>

      <Section title="Liga rasmi">
        <LeagueLogoPicker
          value={meta.leagueLogoUrl ?? null}
          onChange={(url) => setMeta((m) => ({ ...m, leagueLogoUrl: url }))}
          {...(state.theme === "premium" && {
            hint: `${DEFAULT_HINT} Premium'da jadval, stats, Match ID va roster'dagi liga logosi ham shu rasmga almashadi.`,
          })}
        />
      </Section>

      {state.theme === "premium" && (
        <Section title="Premium dizayn">
          <ColorRow
            label="Asosiy rang"
            value={premium.primary}
            fallback="#0a1630"
            onChange={(v) => setPremium({ primary: v })}
          />
          <ColorRow
            label="Urg'u rangi"
            value={premium.accent}
            fallback="#12c979"
            onChange={(v) => setPremium({ accent: v })}
          />
          <span className="text-[12px] text-ink-muted">Homiy logosi</span>
          <LeagueLogoPicker
            value={premium.sponsorLogoUrl}
            onChange={(url) => setPremium({ sponsorLogoUrl: url })}
            emptyLabel="Artel"
            resetLabel="Standart homiyga qaytarish"
            hint="Premium'da Artel o'rniga chiqadi (Match ID, roster, jadval, stats). PNG, JPG, WebP yoki SVG, 5 MB gacha."
          />
        </Section>
      )}

      <Section title="Universal lower-third">
        <div className="grid grid-cols-[120px_1fr_1fr] items-center gap-2">
          <span className="text-[12px] text-ink-muted">Ism / Familiya</span>
          <input
            className={INPUT}
            value={meta.universal?.firstName ?? ""}
            onChange={(e) =>
              setMeta((m) => ({
                ...m,
                universal: { ...emptyUniversal(m.universal), firstName: e.target.value },
              }))
            }
            placeholder="Ism"
          />
          <input
            className={INPUT}
            value={meta.universal?.lastName ?? ""}
            onChange={(e) =>
              setMeta((m) => ({
                ...m,
                universal: { ...emptyUniversal(m.universal), lastName: e.target.value },
              }))
            }
            placeholder="Familiya"
          />
        </div>
        <Field label="Lavozim">
          <input
            className={INPUT}
            value={meta.universal?.label ?? ""}
            onChange={(e) =>
              setMeta((m) => ({
                ...m,
                universal: { ...emptyUniversal(m.universal), label: e.target.value },
              }))
            }
            placeholder="masalan: Komissar"
          />
        </Field>
      </Section>

      <Section title="Ob-havo">
        <div className="grid grid-cols-3 gap-2">
          <NumberField
            label="Harorat °C"
            value={weather.temperature}
            onChange={(v) => setWeather((w) => ({ ...w, temperature: v }))}
          />
          <NumberField
            label="Namlik %"
            value={weather.humidity}
            onChange={(v) => setWeather((w) => ({ ...w, humidity: v }))}
          />
          <NumberField
            label="Shamol m/s"
            value={weather.wind}
            onChange={(v) => setWeather((w) => ({ ...w, wind: v }))}
          />
        </div>
        <Field label="Holat (ikonka)">
          <div role="radiogroup" aria-label="Ob-havo holati" className="flex flex-wrap gap-1.5">
            {WEATHER_CONDITIONS.map((c) => {
              const active = weatherConditionId(weather.condition) === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  title={c.label}
                  onClick={() => setWeather((w) => ({ ...w, condition: c.id }))}
                  className={cn(
                    focusRing,
                    "flex h-14 w-16 flex-col items-center justify-center gap-0.5 rounded-md border text-[10.5px]",
                    active
                      ? "border-accent bg-accent/15 text-ink"
                      : "border-border bg-white text-ink-muted hover:border-ink-faint",
                  )}
                >
                  <img src={weatherIconUrl(c.id)} alt="" className="h-7 w-7" />
                  {c.label}
                </button>
              );
            })}
          </div>
        </Field>
      </Section>
    </>
  );

  const actions = (
    <>
      <Button variant="ghost" onClick={onClose}>
        Bekor
      </Button>
      <Button variant="primary" onClick={save}>
        Saqlash
      </Button>
    </>
  );

  if (inline) {
    return (
      <Card
        title="Match sozlamalari"
        action={<div className="flex items-center gap-2">{actions}</div>}
        bodyClassName="max-h-[calc(100vh-520px)] min-h-[280px] overflow-y-auto"
      >
        <div className="flex flex-col gap-6">{fields}</div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6 pb-16">
      {fields}
      {/* Sticky save bar */}
      <div className="fixed bottom-0 right-0 flex w-full max-w-2xl items-center justify-end gap-2 border-t border-divider bg-surface/95 px-5 py-3 backdrop-blur">
        {actions}
      </div>
    </div>
  );
}

function emptyUniversal(u: { firstName: string; lastName: string; label: string } | null) {
  return u ?? { firstName: "", lastName: "", label: "" };
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-muted">
        {title}
      </h3>
      <div className="flex flex-col gap-2">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-2">
      <span className="text-[12px] text-ink-muted">{label}</span>
      {children}
    </div>
  );
}

function PersonRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: PersonName | null;
  onChange: (v: PersonName | null) => void;
}) {
  const first = value?.firstName ?? "";
  const last = value?.lastName ?? "";
  const update = (f: string, l: string) =>
    onChange(!f && !l ? null : { ...value, firstName: f, lastName: l });
  return (
    <div className="grid grid-cols-[120px_1fr_1fr] items-center gap-2">
      <span className="text-[12px] text-ink-muted">{label}</span>
      <input
        className={INPUT}
        value={first}
        onChange={(e) => update(e.target.value, last)}
        placeholder="Ism"
      />
      <input
        className={INPUT}
        value={last}
        onChange={(e) => update(first, e.target.value)}
        placeholder="Familiya"
      />
    </div>
  );
}

function ColorRow({
  label,
  value,
  fallback,
  onChange,
}: {
  label: string;
  value: string | null;
  fallback: string;
  onChange: (v: string | null) => void;
}) {
  return (
    <div className="grid grid-cols-[120px_auto_auto_1fr] items-center gap-2">
      <span className="text-[12px] text-ink-muted">{label}</span>
      <input
        type="color"
        aria-label={label}
        className="h-8 w-10 cursor-pointer rounded-md border border-border bg-surface p-0.5"
        value={value ?? fallback}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="font-mono text-[12px] text-ink-muted">{value ?? fallback}</span>
      <div>
        {value && (
          <Button variant="ghost" size="sm" onClick={() => onChange(null)}>
            Standart
          </Button>
        )}
      </div>
    </div>
  );
}

function SponsorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: { name: string; tagline: string | null; logoUrl?: string | null } | null;
  onChange: (v: { name: string; tagline: string | null; logoUrl: string | null } | null) => void;
}) {
  const name = value?.name ?? "";
  const tagline = value?.tagline ?? "";
  const logoUrl = value?.logoUrl ?? "";
  const update = (n: string, t: string, l: string) =>
    onChange(!n && !t && !l ? null : { name: n, tagline: t || null, logoUrl: l || null });
  return (
    <div className="grid grid-cols-[120px_1fr_1fr_1fr] items-center gap-2">
      <span className="text-[12px] text-ink-muted">{label}</span>
      <input
        className={INPUT}
        value={name}
        onChange={(e) => update(e.target.value, tagline, logoUrl)}
        placeholder="Nomi"
      />
      <input
        className={INPUT}
        value={tagline}
        onChange={(e) => update(name, e.target.value, logoUrl)}
        placeholder="Izoh"
      />
      <input
        className={INPUT}
        value={logoUrl}
        onChange={(e) => update(name, tagline, e.target.value)}
        placeholder="Logo URL"
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] text-ink-muted">{label}</span>
      <input
        type="number"
        className={INPUT}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    </label>
  );
}
